import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import LuxuryLoading from '../ui/LuxuryLoading';
import { AuthService } from '../../api/auth.api';

const ProtectedRoute = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [loading, setLoading] = useState(true);
    const location = useLocation();
    const authCheckCompleted = useRef(false);

    useEffect(() => {
        // Prevent multiple auth checks
        if (authCheckCompleted.current) {
            setLoading(false);
            return;
        }

        const checkAuth = async () => {
            try {
                authCheckCompleted.current = true;
                console.log('ProtectedRoute: Checking authentication...');

                // Check if we have any stored user data
                const storedUser = AuthService.getStoredUser();

                if (!storedUser) {
                    console.log('ProtectedRoute: No stored user found');
                    setIsAuthenticated(false);
                    setLoading(false);
                    return;
                }

                // Check if token is expired
                if (AuthService.isTokenExpired()) {
                    console.log('ProtectedRoute: Token expired, attempting refresh...');
                    try {
                        await AuthService.refreshToken();
                        console.log('ProtectedRoute: Token refreshed successfully');
                    } catch (refreshError) {
                        console.log('ProtectedRoute: Token refresh failed:', refreshError);
                        // Clear invalid tokens
                        AuthService.logout();
                        setIsAuthenticated(false);
                        setLoading(false);
                        return;
                    }
                }

                // Verify authentication with server
                console.log('ProtectedRoute: Verifying with server...');
                const authStatus = await AuthService.checkAuth();

                if (authStatus.authenticated) {
                    console.log('ProtectedRoute: User authenticated');
                    setIsAuthenticated(true);
                } else {
                    console.log('ProtectedRoute: Server authentication failed');
                    setIsAuthenticated(false);
                }
            } catch (error) {
                console.log('ProtectedRoute: Auth check failed:', error);
                setIsAuthenticated(false);
            } finally {
                setLoading(false);
            }
        };

        checkAuth();

        // Cleanup
        return () => {
            authCheckCompleted.current = false;
        };
    }, []);

    if (loading) {
        return <LuxuryLoading message="Verifying your access..." />;
    }

    if (!isAuthenticated) {
        console.log('ProtectedRoute: Not authenticated, redirecting to login');
        // Only redirect if we're not already on the login page
        if (location.pathname !== '/login') {
            return <Navigate to="/login" state={{ from: location }} replace />;
        }
        return null;
    }

    console.log('ProtectedRoute: User authenticated, rendering children');
    return children;
};

export default ProtectedRoute;